import { useMutation } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { AnimatePresence, motion } from "framer-motion"
import {
	ChevronLeft,
	ChevronRight,
	FileText,
	FileWarning,
	Maximize2,
	Play,
	Plus,
	X,
} from "lucide-react"
import { useId, useState } from "react"
import { toast } from "sonner"
import { deletePostMedia, reorderPostMedia, setVideoPoster } from "@/entities/admin-post"
import type { Media } from "@/shared/lib/types"
import { TRANSITION } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { formatDuration, type MediaItem, toMediaItems } from "../model/media-items"
import { ACCEPTED_MEDIA, type StagedMedia } from "../model/staged-media"
import { MediaViewer } from "./media-viewer"

/** Больше Telegram в одно сообщение не возьмёт. */
const ALBUM_LIMIT = 10

function Tile({
	item,
	index,
	canMove,
	onOpen,
	onMove,
	onRemove,
}: {
	item: MediaItem
	index: number
	/** Файл двигается только среди своих: загруженные отдельно от новых. */
	canMove: { back: boolean; forward: boolean }
	onOpen: () => void
	onMove: (direction: -1 | 1) => void
	onRemove: () => void
}) {
	const duration = formatDuration(item.duration)
	const [imageFailed, setImageFailed] = useState(false)
	return (
		<motion.li
			layout
			initial={{ opacity: 0, scale: 0.96 }}
			animate={{ opacity: 1, scale: 1 }}
			exit={{ opacity: 0, scale: 0.96 }}
			transition={TRANSITION.base}
			className="group relative aspect-video overflow-hidden rounded-xl bg-muted ring-1 ring-border"
		>
			<button
				type="button"
				onClick={onOpen}
				aria-label={`Открыть ${item.name}`}
				className="absolute inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
			>
				{item.posterUrl && !imageFailed ? (
					<img
						src={item.posterUrl}
						alt=""
						onError={() => setImageFailed(true)}
						className="size-full object-cover"
					/>
				) : item.kind === "video" && !imageFailed ? (
					<video
						src={`${item.url}#t=0.5`}
						onError={() => setImageFailed(true)}
						preload="metadata"
						muted
						playsInline
						className="pointer-events-none size-full object-cover"
					/>
				) : (
					<span className="flex size-full flex-col items-center justify-center gap-2 px-3 text-muted-foreground">
						{imageFailed ? <FileWarning className="size-6" /> : <FileText className="size-6" />}
						<span className="line-clamp-2 text-center text-xs break-all">{item.name}</span>
					</span>
				)}
				{item.kind === "video" ? (
					<span className="absolute inset-0 grid place-items-center">
						<span className="grid size-9 place-items-center rounded-full bg-black/55 text-white backdrop-blur transition-transform group-hover:scale-110">
							<Play className="ml-0.5 size-4 fill-current" />
						</span>
					</span>
				) : null}
			</button>

			<div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-2 pt-6 text-[11px] font-medium text-white">
				<span className="rounded bg-black/40 px-1.5 py-0.5 tabular-nums">{index + 1}</span>
				<span className="flex items-center gap-1.5">
					{item.source === "staged" ? (
						<span className="rounded bg-primary/80 px-1.5 py-0.5">новый</span>
					) : null}
					{duration ? <span className="rounded bg-black/40 px-1.5 py-0.5 tabular-nums">{duration}</span> : null}
				</span>
			</div>

			<div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
				{[
					{ label: "Раньше", icon: ChevronLeft, onClick: () => onMove(-1), hidden: !canMove.back },
					{ label: "Позже", icon: ChevronRight, onClick: () => onMove(1), hidden: !canMove.forward },
					{ label: "Открыть", icon: Maximize2, onClick: onOpen },
					{ label: "Убрать", icon: X, onClick: onRemove },
				]
					.filter((action) => !action.hidden)
					.map(({ label, icon: Icon, onClick }) => (
						<button
							key={label}
							type="button"
							aria-label={`${label}: ${item.name}`}
							title={label}
							onClick={onClick}
							className="grid size-7 place-items-center rounded-lg bg-black/60 text-white backdrop-blur transition-colors hover:bg-black/80"
						>
							<Icon className="size-3.5" />
						</button>
					))}
			</div>
		</motion.li>
	)
}

/**
 * Файлы поста. Уже загруженные меняются сразу: удаление, порядок, обложка видео. Новые
 * ждут сохранения поста и уходят вместе с ним.
 */
export function MediaManager({
	postId,
	existing,
	staged,
	onStage,
	onRemoveStaged,
	onMoveStaged,
	onStagedPoster,
	onExistingChanged,
}: {
	postId?: string
	existing: Media[]
	staged: StagedMedia[]
	onStage: (files: File[]) => void
	onRemoveStaged: (key: string) => void
	onMoveStaged: (key: string, direction: -1 | 1) => void
	onStagedPoster: (key: string, file: File) => Promise<void>
	onExistingChanged: () => Promise<unknown>
}) {
	const inputId = useId()
	const { confirm } = useConfirm()
	const [dragging, setDragging] = useState(false)
	const [openKey, setOpenKey] = useState<string | null>(null)
	const items = toMediaItems(existing, staged)
	const open = items.find((item) => item.key === openKey) ?? null
	const overLimit = items.filter((item) => item.kind !== "file").length > ALBUM_LIMIT

	const onError = (fallback: string) => (error: unknown) =>
		toast.error(extractApiErrorMessage(error, fallback))

	const remove = useMutation({
		mutationFn: (mediaId: string) => deletePostMedia(postId!, mediaId),
		onSuccess: async () => {
			toast.success("Файл убран из поста")
			await onExistingChanged()
		},
		onError: onError("Не удалось убрать файл"),
	})
	const reorder = useMutation({
		mutationFn: (ids: string[]) => reorderPostMedia(postId!, ids),
		onSuccess: () => onExistingChanged(),
		onError: onError("Не удалось поменять порядок"),
	})
	const poster = useMutation({
		mutationFn: ({ mediaId, file }: { mediaId: string; file: File }) =>
			setVideoPoster(postId!, mediaId, file),
		onSuccess: async () => {
			toast.success("Обложка видео обновлена")
			await onExistingChanged()
		},
		onError: onError("Не удалось поменять обложку"),
	})

	const move = (item: MediaItem, direction: -1 | 1) => {
		if (item.source === "staged") return onMoveStaged(item.key, direction)
		const ids = existing.map((media) => media.id)
		const from = ids.indexOf(item.key)
		const to = from + direction
		if (to < 0 || to >= ids.length) return
		;[ids[from], ids[to]] = [ids[to]!, ids[from]!]
		reorder.mutate(ids)
	}

	const askRemove = (item: MediaItem) => {
		if (item.source === "staged") {
			if (openKey === item.key) setOpenKey(null)
			return onRemoveStaged(item.key)
		}
		confirm({
			title: "Убрать файл из поста?",
			message:
				"Файл удалится из хранилища. В уже отправленном сообщении канала он останется, пока пост не отправить заново.",
			confirmText: "Убрать",
			onConfirm: async () => {
				setOpenKey(null)
				await remove.mutateAsync(item.key).catch(() => undefined)
			},
		})
	}

	const setPoster = async (item: MediaItem, file: File) => {
		if (item.source === "staged") return onStagedPoster(item.key, file)
		await poster.mutateAsync({ mediaId: item.key, file }).catch(() => undefined)
	}

	const take = (files: FileList | null) => {
		if (files?.length) onStage(Array.from(files))
	}

	return (
		<div
			onDragOver={(event) => {
				if (!event.dataTransfer.types.includes("Files")) return
				event.preventDefault()
				setDragging(true)
			}}
			onDragLeave={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false)
			}}
			onDrop={(event) => {
				event.preventDefault()
				setDragging(false)
				take(event.dataTransfer.files)
			}}
			className={cn(
				"-m-2 rounded-2xl p-2 transition-colors",
				dragging && "bg-primary/5 ring-2 ring-primary/50 ring-dashed",
			)}
		>
			<input
				id={inputId}
				type="file"
				multiple
				accept={ACCEPTED_MEDIA}
				className="sr-only"
				onChange={(event) => {
					take(event.target.files)
					event.target.value = ""
				}}
			/>
			<ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
				<AnimatePresence initial={false}>
					{items.map((item, index) => (
						<Tile
							key={item.key}
							item={item}
							index={index}
							canMove={
								item.source === "existing"
									? { back: index > 0, forward: index < existing.length - 1 }
									: { back: index > existing.length, forward: index < items.length - 1 }
							}
							onOpen={() => setOpenKey(item.key)}
							onMove={(direction) => move(item, direction)}
							onRemove={() => askRemove(item)}
						/>
					))}
				</AnimatePresence>
				<li className="aspect-video">
					<label
						htmlFor={inputId}
						className="flex size-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-border text-sm text-muted-foreground transition-colors hover:border-foreground/30 hover:bg-accent/40 hover:text-foreground"
					>
						<Plus className="size-5" />
						<span className="font-medium">Добавить файлы</span>
						<span className="text-xs">или перетащите сюда</span>
					</label>
				</li>
			</ul>
			{overLimit ? (
				<p className="mt-3 text-xs text-amber-300">
					В одно сообщение Telegram берёт до {ALBUM_LIMIT} фото и видео. Лишние в канал не уйдут.
				</p>
			) : null}
			<MediaViewer
				item={open}
				onClose={() => setOpenKey(null)}
				onPoster={setPoster}
				onRemove={askRemove}
				busy={remove.isPending || poster.isPending}
			/>
		</div>
	)
}
